// Refine shadcn/ui registry `sign-in-form`, trimmed to what the admin API
// supports: email + password with "remember me" and a link to reset a
// forgotten password. No social providers and no self-service sign-up
// (invitation only).

import type { FormEvent } from 'react'
import { useState } from 'react'

import { useLogin } from '@refinedev/core'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { AuthCard } from '~/components/refineUi/form/authCard'
import { DevAccountPicker } from '~/components/refineUi/form/devAccountPicker'
import { InputPassword } from '~/components/refineUi/form/inputPassword'
import { Button } from '~/components/ui/button'
import { Checkbox } from '~/components/ui/checkbox'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { Separator } from '~/components/ui/separator'
import { cn } from '~/lib/utils'
import type { LoginParams } from '~/providers/authProvider'
import { FORGOT_PASSWORD_PATH } from '~/providers/authProvider'

export const SignInForm = () => {
  const { t } = useTranslation()
  const [remember, setRemember] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const { mutate: login, isPending } = useLogin<LoginParams>()

  const handleSignIn = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    login({ email, password, remember })
  }

  return (
    <AuthCard
      title={t('auth.signIn.title')}
      description={t('auth.signIn.description')}
    >
      {import.meta.env.DEV && (
        <>
          <DevAccountPicker
            disabled={isPending}
            onPick={(credentials) => {
              setEmail(credentials.email)
              setPassword(credentials.password)
              login({ ...credentials, remember })
            }}
          />
          <Separator className={cn('my-6')} />
        </>
      )}
      <form onSubmit={handleSignIn}>
        <div className={cn('flex', 'flex-col', 'gap-2')}>
          <Label htmlFor="email">{t('auth.signIn.email')}</Label>
          <Input
            id="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <div className={cn('relative', 'flex', 'flex-col', 'gap-2', 'mt-6')}>
          <div className={cn('flex', 'items-center', 'justify-between')}>
            <Label htmlFor="password">{t('auth.signIn.password')}</Label>
            <Link
              to={FORGOT_PASSWORD_PATH}
              className={cn(
                'text-sm',
                'text-blue-600',
                'dark:text-blue-400',
                'hover:underline',
              )}
            >
              {t('auth.signIn.forgotPassword')}
            </Link>
          </div>
          <InputPassword
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </div>

        <div className={cn('flex items-center', 'space-x-2', 'mt-4')}>
          <Checkbox
            id="remember"
            checked={remember}
            onCheckedChange={(checked) => setRemember(checked === true)}
          />
          <Label htmlFor="remember">{t('auth.signIn.rememberMe')}</Label>
        </div>

        <Button
          type="submit"
          size="lg"
          className={cn('w-full', 'mt-6')}
          disabled={isPending}
        >
          {t('auth.signIn.submit')}
        </Button>
      </form>
    </AuthCard>
  )
}

SignInForm.displayName = 'SignInForm'
